import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  InterestRateEligibilityCriteriaDto,
  InterestRateFilterKey,
  InterestRateKey,
} from 'api/Interest-Rates/InterestRateDto'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { createFilterNormalizerWithMapping } from 'features/DataGrid/helpers/normalizeFilter'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { getCurrenciesAction } from 'store/trading-accounts/trading-accounts.actions'
import { fetchInterestRatesEligibilityTypes } from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import {
  TableStateContext,
  TableStateContextAction,
  TableStateContextData,
} from 'utils/TableStateContext'

import {
  AddInterestRateEligibilityForm,
  AddInterestRateEligibilityFormValues,
} from './AddInterestRateEligibilityForm'
import {
  AdministrationAllowedInterestRatesNavigation,
  AdministrationAllowedInterestRatesTab,
} from './AdministrationInterestRatesNavigation'
import {
  EditInterestRateEligibilityForm,
  EditInterestRateEligibilityFormValues,
} from './EditInterestRateEligibilityForm'
import interestRatesEligibilityColumns from './InterestRatesEligibilityColumns'

const useMapStateToProps = () => ({
  currencies: useAppSelector((state) => state.tradingAccounts.currencies),
  interestRateEligibilityCriteriaType: useAppSelector(
    (state) => state.transactions.interestRateEligibilityCriteriaType
  ),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchInterestRatesEligibilityCriteriaTypes: async () =>
      dispatch(fetchInterestRatesEligibilityTypes(walletsApiClient)),
    fetchCurrencies: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
  }
}

const useInterestRatesFetch = (): InterestRatesFilterProps => {
  const { fetchInterestRatesEligibilityCriteriaTypes, fetchCurrencies } = useMapDispatchToProps()
  const { interestRateEligibilityCriteriaType, currencies } = useMapStateToProps()

  const interestRatesFilter = useInterestRatesFilter()

  useEffect(() => {
    if (isEmpty(interestRateEligibilityCriteriaType)) {
      fetchInterestRatesEligibilityCriteriaTypes()
    }
    if (isEmpty(currencies)) {
      fetchCurrencies()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return interestRatesFilter
}

interface InterestRatesFilterProps {
  data: PageData<InterestRateEligibilityCriteriaDto[]> | undefined
  tableData: TableStateContextData
  setTableData: TableStateContextAction
  pageIndex: number
  pageSize: number
  isLoading: boolean
  forceRefresh(): void
  filter: CompositeFilterDescriptor
  setFilter(value: CompositeFilterDescriptor): void
  pagination: Page

  setPagination(page: Page | ((x: Page) => Page)): void
  handleFilterChange(event: GridFilterChangeEvent): void
  handlePageChange(event: GridPageChangeEvent): void
}

const useInterestRatesFilter = (): InterestRatesFilterProps => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => {
      return walletsApiClient.getInterestRatesEligibility({
        search: getSearchQuery(),
        pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
        pageSize: paginationState.take,
        skipCount: tableData?.skipCount,
      })
    },
    [paginationState, tableData?.skipCount] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  const getSearchQuery = useCallback(
    () => {
      const filterSearchQuery = normalizeSearchQuery(filter)
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: filterSearchQuery.filters,
        }) ?? null
      )
    },
    [filter.filters] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableData((props) => {
      return { ...props, skipCount: tableData?.skipCount, meta: data }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount])

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  return {
    data,
    tableData,
    setTableData,
    pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
    pageSize: paginationState.take ?? 10,
    isLoading,
    forceRefresh,
    filter,
    setFilter,
    pagination: paginationState,
    setPagination: setPaginationState,
    handleFilterChange,
    handlePageChange,
  }
}

const useTablePager = (props: InterestRatesFilterProps) => {
  const { data, pagination, setPagination, tableData, setTableData } = props

  useEffect(() => {
    setTableData((state) => {
      return { ...state, skipCount: tableData?.skipCount, meta: data }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount])

  const handlePageChangePager = (pagerIndex: number) => {
    setPagination((state) => {
      return { ...state, skip: calculateSkipIndex(pagerIndex, pagination.take) }
    })
  }

  const handleSizeChangePager = (take: number) => {
    setPagination((state) => {
      return { ...state, skip: 0, take }
    })
  }

  const handleSkipCountChangePager = (skipCount: boolean) => {
    setTableData((state) => {
      return { ...state, skipCount }
    })
  }

  return { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager }
}

interface ModalProps {
  data: InterestRateEligibilityCriteriaDto | undefined
  visible: boolean
}

const initialModalState = () => ({
  data: undefined,
  visible: false,
})

const InterestRatesEligibilityPage = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const { interestRateEligibilityCriteriaType, currencies } = useMapStateToProps()

  const interestRates = useInterestRatesFetch()
  const tablePager = useTablePager(interestRates)

  const { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager } = tablePager
  const { data, isLoading, filter, pagination, tableData, pageIndex, pageSize } = interestRates
  const { forceRefresh, handlePageChange, handleFilterChange, setTableData } = interestRates

  const [addModal, setAddModal] = useState(false)
  const [editModal, setEditModal] = useState<ModalProps>(initialModalState())
  const [deleteInterestRateModal, setDeleteInterestRateModal] =
    useState<ModalProps>(initialModalState())

  const permissions = auth?.permissions || []

  const handleEditCancel = () => {
    setEditModal({ data: undefined, visible: false })
  }

  const handleAddCancel = () => {
    setAddModal(false)
  }

  const handleConfirmationCancel = () => {
    setDeleteInterestRateModal({ visible: false, data: undefined })
  }

  const handleAddInterestRateThreshold = async (values: AddInterestRateEligibilityFormValues) => {
    try {
      await walletsApiClient.addInterestRatesEligibility(values)
      setToast(successToast('Interest rate eligibility has been added.'))
      forceRefresh()
    } catch (error) {
      console.error(error)
    } finally {
      setAddModal(false)
    }
  }

  const handleEditInterestRateThreshold = async (values: EditInterestRateEligibilityFormValues) => {
    try {
      await walletsApiClient.updateInterestRatesEligibility(values)
      setToast(successToast('Interest rate eligibility has been updated.'))
      forceRefresh()
    } catch (error) {
      console.error(error)
    } finally {
      setEditModal({ data: undefined, visible: false })
    }
  }

  const handleConfirmationDelete = async () => {
    if (deleteInterestRateModal.data) {
      try {
        await walletsApiClient.deleteInterestRatesEligibility(deleteInterestRateModal.data.id)
        setToast(successToast('Intest rate eligibility has been deleted.'))
        handleEditCancel()
        handleConfirmationCancel()
      } catch (error) {
        console.error(error)
        handleEditCancel()
        handleConfirmationCancel()
      } finally {
        forceRefresh()
      }
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Interest Rates Thresholds',
            link: links.administrationInterestRateEligibility.url,
          },
        ]}
      />
      <AdministrationAllowedInterestRatesNavigation
        tab={AdministrationAllowedInterestRatesTab.ELIGIBILITY_CRITERIA}
      />
      <AddButton
        text='Add new Eligibility Criteria +'
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditInterestRatesConfig)}
        onClick={() => setAddModal(true)}
        data-test='add-new-eligibility-button'
      />
      {deleteInterestRateModal.data && (
        <ConfirmationModal
          yesButtonText='Yes, delete'
          onYes={handleConfirmationDelete}
          onCancel={handleConfirmationCancel}
          visible={deleteInterestRateModal.visible}
          renderTitle={<ConfirmationDeleteInterestRateModal {...deleteInterestRateModal} />}
        />
      )}
      {addModal && (
        <Modal visible={addModal} onClose={handleAddCancel}>
          <AddInterestRateEligibilityForm
            currencies={currencies}
            permissions={permissions}
            types={interestRateEligibilityCriteriaType}
            onSubmit={handleAddInterestRateThreshold}
          />
        </Modal>
      )}
      {editModal.visible && (
        <Modal visible={editModal.visible} onClose={handleEditCancel}>
          <EditInterestRateEligibilityForm
            currencies={currencies}
            data={editModal.data as InterestRateEligibilityCriteriaDto}
            permissions={permissions}
            types={interestRateEligibilityCriteriaType}
            onSubmit={handleEditInterestRateThreshold}
          />
        </Modal>
      )}
      {data && (
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            filterable
            resizable
            data={data.items}
            filter={filter}
            take={pagination.take}
            skip={pagination.skip}
            total={data.totalResults}
            onPageChange={handlePageChange}
            onFilterChange={handleFilterChange}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChangePager}
                onPageChange={handlePageChangePager}
                onPageSizeChange={handleSizeChangePager}
                page={pageIndex}
                pageSize={pageSize}
                take={pagination.take || 10}
                skip={pagination.skip || 0}
              />
            )}
          >
            {interestRatesEligibilityColumns
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            {hasPermission(permissions, PermissionsEnum.CanEditInterestRatesConfig) && (
              <Column
                width={50}
                title=''
                cell={(props: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          'data-test': 'edit',
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanEditInterestRatesConfig
                          ),
                          onClick: () =>
                            setEditModal({
                              visible: true,
                              data: props.dataItem,
                            }),
                        },
                        {
                          name: 'Remove',
                          'data-test': 'remove',
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanEditInterestRatesConfig
                          ),
                          onClick: () =>
                            setDeleteInterestRateModal({
                              visible: true,
                              data: props.dataItem,
                            }),
                        },
                      ]}
                      data-test='more-button'
                    />
                  </td>
                )}
                filterable={false}
              />
            )}
          </Grid>
        </TableStateContext.Provider>
      )}
    </>
  )
}

const ConfirmationDeleteInterestRateModal: React.FC<ModalProps> = (props) => {
  const { data } = props

  return (
    <div>
      <p>
        Are you sure you want to
        <br />
        delete the following eligibility criteria record?
      </p>

      <div>
        <div id='field'>
          <strong>Eligibility</strong> {data?.eligibilityCriteriaType?.name}
        </div>
        <div id='field'>
          <strong>Value</strong> {data?.value}
        </div>
      </div>
    </div>
  )
}

const normalizeSearchQuery = (filterDescriptor: CompositeFilterDescriptor) => {
  const fieldMappings: { [key: string]: InterestRateFilterKey } = {
    [InterestRateKey.EligibilityCriteriaType]: InterestRateFilterKey.ELIGIBILITY_CRITERIA_TYPE,
  }

  const normalizeFilters = createFilterNormalizerWithMapping(fieldMappings)
  const filters = normalizeFilters(filterDescriptor)

  return { filters }
}

export default InterestRatesEligibilityPage
